import React from 'react';

const Position = {
    Above: 'above',
    Below: 'below',
    Visible: 'visible'
}

function Landmark(props) {
    const containerRef = React.useRef(undefined);
    const [id, setId] = React.useState(undefined);
    React.useEffect(() => setId(new Date().getTime()), []);
    if (!id) {
        return null;
    }
    containerRef.current = props.containerRef ?? window.document.body;
    return <_Landmark {...props} containerRef={containerRef} />
}

function _Landmark(props) {
    const landmarkRef = React.useRef(undefined);

    // Scroll listener
    const [scrollInfo, setScrollInfo] = React.useState({ x: 0, y: 0 });
    const scrollEventListenerAdded = React.useRef(false);

    const scrollDependencyList = [];
    if (!props.useWindow) {
        scrollDependencyList.push(props.containerRef);
    }
    const handleScroll = (e) => {
        if (!e || !e.target) {
            return;
        }
        const x = props.containerRef.current.scrollLeft ?? window.scrollX;
        const y = props.containerRef.current.scrollTop ?? window.scrollY;
        setScrollInfo({ x, y });
    };
    React.useEffect(() => {
        const containerElement = props.useWindow ? window : props.containerRef.current;
        if (containerElement && scrollEventListenerAdded.current === false) {
            containerElement.addEventListener('scroll', handleScroll, { passive: true });
        }
        scrollEventListenerAdded.current = true;

        return () => {
            if (scrollEventListenerAdded && containerElement) {
                containerElement.removeEventListener('scroll', handleScroll);
            }
            scrollEventListenerAdded.current = false;
        };
    }, scrollDependencyList);

    // Resize listener
    const getDimensions = () => {
        if (props.onDimensions) {
            return props.onDimensions();
        }
        const containerElement = props.containerRef.current;
        if (!containerElement) {
            return { height: window.innerHeight, width: window.innerWidth };
        }
        return { height: containerElement.offsetHeight, width: containerElement.offsetWidth };
    }
    const [containerRect, setContainerRect] = React.useState({ bottom: 0, left: 0, right: 0, top: 0 });
    const [dimensions, setDimensions] = React.useState(getDimensions());
    const resizeTimeout = React.useRef(undefined);
    const resizeEventListenerAdded = React.useRef(false);
    const updateDimensions = () => {
        setDimensions(getDimensions())
        if (props.containerRef && props.containerRef.current) {
            const rect = props.containerRef.current.getBoundingClientRect();
            if (props.useWindow) {
                rect.y += window.scrollY;
            }
            setContainerRect(rect);
        }
    }

    React.useEffect(() => {
        updateDimensions();

        function handleResize() {
            if (resizeTimeout.current) {
                clearTimeout(resizeTimeout.current);
            }
            resizeTimeout.current = setTimeout(updateDimensions, props.resizeTimeout || 15);
        }
        if (resizeEventListenerAdded.current === false) {
            window.addEventListener('resize', handleResize, { passive: true });
        }
        resizeEventListenerAdded.current = true;

        return () => {
            window.removeEventListener('resize', handleResize);
            resizeEventListenerAdded.current = false;
        };
    }, [props.containerRef]);

    // Position handling
    const previousPositionRef = React.useRef(undefined);
    const [delayed, setDelayed] = React.useState(false);
    const [discovered, setDiscovered] = React.useState(false);
    const [position, setPosition] = React.useState(undefined);

    React.useLayoutEffect(() => {
        if (delayed) {
            return;
        }
        if (landmarkRef.current) {
            const rect = landmarkRef.current.getBoundingClientRect();
            const offsetBottom = (props.offsetBottom || 0) - containerRect.top;
            const offsetLeft = (props.offsetLeft || 0) + containerRect.left;
            const offsetRight = (props.offsetRight || 0) - containerRect.left;
            const offsetTop = (props.offsetTop || 0) + containerRect.top;
            if (rect.top >= offsetTop &&
                rect.bottom < dimensions.height - offsetBottom &&
                rect.left >= offsetLeft &&
                rect.right < dimensions.width - offsetRight) {
                setPosition(Position.Visible);
            } else if (rect.top > dimensions.height - offsetBottom) {
                setPosition(Position.Below);
            } else {
                setPosition(Position.Above);
            }
        }
    }, [landmarkRef, scrollInfo, dimensions, delayed]);

    React.useLayoutEffect(() => {
        if (delayed) {
            return;
        }
        if (previousPositionRef.current === undefined && position !== undefined) {
            previousPositionRef.current = position;

            if (position == Position.Visible) {
                // Visible on load
                if (props.onEnter) {
                    if (!discovered) {
                        setDelayed(true);
                        setPosition(undefined);
                        previousPositionRef.current = undefined;
                        setTimeout(() => setDelayed(false), 0);
                    } else {
                        props.onEnter();
                    }
                }
                setDiscovered(true);
            }
        }
        const _enter = () => {
            setDiscovered(true);

            if (props.onEnter) {
                props.onEnter(previousPositionRef.current);
            }
        }
        const _leave = () => {
            if (props.onLeave) {
                props.onLeave(position);
            }
        }
        if (position === Position.Visible && (previousPositionRef.current === Position.Below || previousPositionRef.current === Position.Above)) {
            // Entered from above or below
            _enter();
            previousPositionRef.current = position;
        }
        if ((position == Position.Above || position == Position.Below) && previousPositionRef.current == Position.Visible) {
            // No longer visible => Update previous position
            previousPositionRef.current = position;
            _leave();
        }
        if ((position == Position.Above && previousPositionRef.current == Position.Below) || (position == Position.Below && previousPositionRef.current == Position.Above)) {
            // Rapid scroll => Fire the events
            _enter();
            _leave();
        }
    }, [position]);

    return <span ref={landmarkRef} style={{ fontSize: 0 }} />
}

export { Landmark as Component, Position };